import React from 'react';
import {
  Box,
  VStack,
  Icon,
  Text,
  useColorModeValue,
  Flex,
} from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiHome,
  FiDollarSign,
  FiUsers,
  FiPieChart,
  FiClock,
  FiBarChart2,
  FiSettings,
  FiHelpCircle,
  FiFileText,
} from 'react-icons/fi';

interface NavItemProps {
  to: string;
  icon: React.ElementType;
  children: React.ReactNode;
}

const NavItem: React.FC<NavItemProps> = ({ to, icon, children }) => {
  const activeBg = useColorModeValue('brand.50', 'brand.900');
  const activeColor = useColorModeValue('brand.600', 'brand.200');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');

  return (
    <NavLink to={to}>
      {({ isActive }) => (
        <Flex
          align="center"
          p={3}
          mx={2}
          borderRadius="lg"
          role="group"
          cursor="pointer"
          bg={isActive ? activeBg : 'transparent'}
          color={isActive ? activeColor : 'inherit'}
          _hover={{
            bg: isActive ? activeBg : hoverBg,
          }}
          transition="all 0.2s"
        >
          <Box
            mr={4}
            fontSize="16"
            as={icon}
            color={isActive ? activeColor : 'inherit'}
          />
          <Text fontWeight={isActive ? 'semibold' : 'medium'}>
            {children}
          </Text>
        </Flex>
      )}
    </NavLink>
  );
};

const Sidebar: React.FC = () => {
  const { t } = useTranslation('common');
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const navItems = [
    { to: '/', icon: FiHome, label: t('navigation.dashboard') },
    { to: '/recharge', icon: FiDollarSign, label: t('navigation.recharge') },
    { to: '/invite', icon: FiUsers, label: t('navigation.invite') },
    { to: '/management', icon: FiPieChart, label: t('navigation.management') },
    { to: '/history', icon: FiClock, label: t('navigation.history') },
    { to: '/reserve', icon: FiBarChart2, label: t('navigation.reserve') },
    { to: '/whitepaper', icon: FiFileText, label: t('navigation.whitepaper') },
    { to: '/settings', icon: FiSettings, label: t('navigation.settings') },
    { to: '/help', icon: FiHelpCircle, label: t('navigation.help') },
  ];

  return (
    <Box
      w={64}
      bg={bg}
      borderRight="1px"
      borderColor={borderColor}
      pt={12}
      pb={6}
      position="fixed"
      top="57px"
      left={0}
      h="calc(100vh - 57px)"
      overflowY="auto"
      zIndex={15}
    >
      <VStack spacing={2} align="stretch">
        {navItems.map((item) => (
          <NavItem key={item.to} to={item.to} icon={item.icon}>
            {item.label}
          </NavItem>
        ))}
      </VStack>
    </Box>
  );
};

export default Sidebar; 